'use client';

import { Button } from '@ahafood/ui/components/button';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@ahafood/ui/components/drawer';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@ahafood/ui/components/sheet';
import { cn } from '@ahm/common-helpers';
import { Settings2Icon } from 'lucide-react';
import type React from 'react';
import { useBottomPosition } from '@/hooks/use-bottom-position';

interface FilterDrawerProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  tempFilters: {
    category_slug: string;
    promotion: boolean;
    freeship: boolean | string;
    special_request: string;
    nearby: boolean;
    open_now: boolean;
  };
  setTempFilters: React.Dispatch<
    React.SetStateAction<{
      category_slug: string;
      promotion: boolean;
      freeship: boolean | string;
      special_request: string;
      nearby: boolean;
      open_now: boolean;
    }>
  >;
  categories: {
    id: number;
    slug: string;
    title_vi_vn: string;
  }[];
  textValue: string;
  activeCount: number;
  onApply: () => void;
  onReset: () => void;
}

function FilterContent({
  tempFilters,
  setTempFilters,
  categories,
  textValue,
  onApply,
  onReset,
}: Omit<FilterDrawerProps, 'isOpen' | 'setIsOpen' | 'activeCount'>) {
  return (
    <>
      <div
        className="px-4 flex-1 overflow-y-auto overscroll-contain"
        style={{ WebkitOverflowScrolling: 'touch' } as React.CSSProperties}
      >
        {/* --- Special Options --- */}
        <div className="mb-6">
          <div className="flex justify-between">
            <h3 className="text-base font-semibold mb-3">Tùy chọn quán</h3>
            <p className="text-sm text-neutral-60 mb-4">Có thể chọn nhiều</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              className={cn(
                'flex-1 px-3 py-2 rounded-lg border',
                tempFilters.nearby && 'border-primary-50'
              )}
              onClick={() =>
                setTempFilters((p) => ({ ...p, nearby: !p.nearby }))
              }
            >
              Gần tôi (dưới 5km)
            </Button>
            <Button
              variant="outline"
              className={cn(
                'flex-1 px-3 py-2 rounded-lg border',
                tempFilters.promotion && 'border-primary-50'
              )}
              onClick={() =>
                setTempFilters((p) => ({ ...p, promotion: !p.promotion }))
              }
            >
              Ưu đãi
            </Button>
            <Button
              variant="outline"
              className={cn(
                'flex-1 px-3 py-2 rounded-lg border',
                tempFilters.open_now && 'border-primary-50'
              )}
              onClick={() =>
                setTempFilters((p) => ({ ...p, open_now: !p.open_now }))
              }
            >
              Mở cửa
            </Button>
          </div>
        </div>

        {/* --- Delivery Fee --- */}
        <div className="mb-6">
          <div className="flex justify-between">
            <h3 className="text-base font-semibold mb-3">Phí giao hàng</h3>
            <p className="text-sm text-neutral-60 mb-4">Chỉ chọn một</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Button
              variant="outline"
              className={cn(
                'flex flex-col gap-2 p-3 rounded-lg border transition-all',
                tempFilters.freeship === 'freeship' && 'border-primary-50'
              )}
              onClick={() =>
                setTempFilters((prev) => ({
                  ...prev,
                  freeship: prev.freeship === 'freeship' ? false : 'freeship',
                }))
              }
            >
              <span className="text-sm font-medium">Freeship</span>
            </Button>

            <Button
              variant="outline"
              className={cn(
                'flex flex-col gap-2 p-3 rounded-lg border transition-all',
                tempFilters.freeship === 'freeship-100' && 'border-primary-50'
              )}
              onClick={() =>
                setTempFilters((prev) => ({
                  ...prev,
                  freeship:
                    prev.freeship === 'freeship-100' ? false : 'freeship-100',
                }))
              }
            >
              <span className="text-sm font-medium">Freeship</span>
            </Button>
          </div>
        </div>

        {/* --- Categories --- */}
        <div className="mb-6">
          <div className="flex justify-between">
            <h3 className="text-base font-semibold mb-3">Danh mục</h3>
            <p className="text-sm text-neutral-60 mb-4">Chỉ chọn một</p>
          </div>
          {textValue && (
            <div className="mb-3 p-2 bg-yellow-10 rounded-lg">
              <p className="text-xs text-yellow-60">
                Không thể chọn danh mục khi đang tìm kiếm bằng từ khóa
              </p>
            </div>
          )}
          <div className="grid grid-cols-3 gap-3">
            {categories.map((category) => (
              <Button
                key={category.id}
                variant="outline"
                disabled={!!textValue}
                onClick={() =>
                  setTempFilters((p) => ({
                    ...p,
                    category_slug:
                      p.category_slug === category.slug ? '' : category.slug,
                  }))
                }
                className={cn(
                  'flex flex-col h-auto p-3',
                  tempFilters.category_slug === category.slug &&
                    'border-primary-50',
                  textValue && 'opacity-50 cursor-not-allowed'
                )}
              >
                <span className="text-xs text-center">
                  {category.title_vi_vn}
                </span>
              </Button>
            ))}
          </div>
        </div>
      </div>

      <div className="px-4 pt-4">
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={onReset}
            className="flex-1 h-12 border-neutral-20 text-neutral-70 hover:bg-neutral-10 bg-transparent"
          >
            Đặt lại
          </Button>
          <Button
            onClick={onApply}
            className="flex-1 h-12 bg-primary-50 hover:bg-primary-60 text-white"
          >
            Áp dụng
          </Button>
        </div>
      </div>
    </>
  );
}

export function FilterDrawer({
  isOpen,
  setIsOpen,
  tempFilters,
  setTempFilters,
  categories,
  textValue,
  activeCount,
  onApply,
  onReset,
}: FilterDrawerProps) {
  const { bottomPosition, isMobile } = useBottomPosition();

  const TriggerButton = (
    <Button
      size="icon"
      className={cn(
        'shrink-0 size-10 rounded-xl relative flex items-center justify-center border border-neutral-20 bg-white shadow-none text-neutral-70 hover:bg-neutral-10 [&_svg]:size-5',
        activeCount > 0 && 'border-primary-50 bg-primary-5 text-primary-60'
      )}
    >
      <Settings2Icon />
      {activeCount > 0 && (
        <span className="absolute top-0 right-0 translate-x-1/3 -translate-y-1/3 bg-primary-50 text-white px-1 rounded-full text-[10px] font-bold min-w-[16px] h-[16px] flex items-center justify-center shadow-sm">
          {activeCount}
        </span>
      )}
    </Button>
  );

  if (isMobile) {
    // Mobile: Use Drawer (bottom sheet)
    return (
      <Drawer open={isOpen} onOpenChange={setIsOpen} repositionInputs={false}>
        <DrawerTrigger asChild>{TriggerButton}</DrawerTrigger>
        <DrawerContent
          className="max-h-[90dvh] flex flex-col"
          style={{ marginBottom: bottomPosition }}
        >
          <DrawerHeader className="pb-2">
            <p className="text-sm text-neutral-60 text-left">Lọc theo</p>
          </DrawerHeader>
          <DrawerTitle className="hidden" />

          <FilterContent
            tempFilters={tempFilters}
            setTempFilters={setTempFilters}
            categories={categories}
            textValue={textValue}
            onApply={onApply}
            onReset={onReset}
          />
        </DrawerContent>
      </Drawer>
    );
  }

  // Desktop: Use Sheet (left side panel)
  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>{TriggerButton}</SheetTrigger>
      <SheetContent
        side="left"
        className="w-[400px] sm:w-[400px] flex flex-col"
      >
        <SheetHeader>
          <SheetTitle>Bộ lọc</SheetTitle>
          <p className="text-sm text-neutral-60 text-left">Lọc theo</p>
        </SheetHeader>

        <FilterContent
          tempFilters={tempFilters}
          setTempFilters={setTempFilters}
          categories={categories}
          textValue={textValue}
          onApply={onApply}
          onReset={onReset}
        />
      </SheetContent>
    </Sheet>
  );
}
